import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Timer, Clock, CheckCircle2, Hourglass, FileText, Trash2, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { requirementTatApi, vacancyApi } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useUnit } from '../contexts/UnitContext';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

/**
 * Recruitment TAT — open positions and how long each one took to fill.
 *
 * "Recruitment", not "Requirement": the figure measures how long RECRUITING
 * took, not a requirement. The file and route still read `requirement-tat`
 * because `recruitment-tat` is taken by the older typed-in register that the
 * KPI still falls back to; renaming those is a separate job.
 *
 * This was two screens. "Vacancies" listed the open seats and "Requirement
 * TAT" measured them, but both read the same `vacancies` collection and a seat
 * simply moves from one list to the other the day somebody joins: an open row
 * here IS a vacancy, a filled row is that same vacancy with its TAT settled.
 * Keeping them apart meant HR checked two places to answer one question, and
 * the two disagreed about which seats counted.
 *
 * So: one page. The Open rows carry everything the Vacancies screen did — the
 * job description and the ability to close a seat that should never have been
 * opened — and the Filled rows carry the measurement. Nothing on it is typed
 * in; every figure comes from the vacancy's own dates and the joiner's start
 * date.
 */

const dmy = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? String(iso).slice(0, 10)
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Colour by how long it took, on the same ladder the Recruitment TAT KPI uses,
// so a row that reads green here cannot score badly there.
const tatColour = (days) => {
  if (days == null) return '#98A1B2';
  if (days <= 30) return '#15654B';
  if (days <= 60) return '#8A5A12';
  return '#B42318';
};

const Kpi = ({ icon: Icon, label, value, sub, color, bg }) => (
  <div className="bg-white border border-[#E6E9F0] rounded-[14px] p-5 shadow-sm">
    <div className="flex items-center justify-between mb-2">
      <p className="text-[10px] font-semibold tracking-wider uppercase text-[#5A6377]">{label}</p>
      <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: bg }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </span>
    </div>
    <p className="text-3xl font-bold tracking-tight text-[#11182B]">{value}</p>
    {sub && <p className="text-[11px] text-[#98A1B2] mt-1">{sub}</p>}
  </div>
);

export default function RequirementTat() {
  const { activeUnit } = useUnit();
  const { hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('all');          // all | open | filled
  // Job description, carried over from the Vacancies screen.
  const [jdVac, setJdVac] = useState(null);
  const [jdText, setJdText] = useState('');
  const [jdMethod, setJdMethod] = useState('');
  const [jdLoading, setJdLoading] = useState(false);

  const canManage = hasPerm('employee.create');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await requirementTatApi.list({ unit: activeUnit });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Recruitment TAT');
    } finally { setLoading(false); }
  }, [activeUnit]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    const q = search.trim().toLowerCase();
    return all.filter((r) => {
      if (view !== 'all' && r.status.toLowerCase() !== view) return false;
      if (!q) return true;
      return [r.vacated_by, r.designation, r.department, r.new_employee_name]
        .some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [data, search, view]);

  const s = data?.summary || {};

  // ---- the two actions the Vacancies screen owned -------------------------
  const removeVac = async (r) => {
    if (!window.confirm(
      `Close the open position "${r.designation}" (${r.department})?\n\n`
      + `This removes the seat from the Org Chart and from this register. `
      + `Anyone reporting to it moves up to its manager.`)) return;
    try {
      await vacancyApi.remove(r.vacancy_id);
      toast.success('Position closed');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not close it'); }
  };

  const openJd = async (r) => {
    setJdVac(r); setJdText(''); setJdMethod(''); setJdLoading(true);
    try {
      const res = await vacancyApi.generateJd(r.vacancy_id, false);
      setJdText(res.data.jd || ''); setJdMethod(res.data.method || '');
    } catch (e) { toast.error('JD load failed'); } finally { setJdLoading(false); }
  };
  const regenJd = async () => {
    if (!jdVac) return;
    setJdLoading(true);
    try {
      const res = await vacancyApi.generateJd(jdVac.vacancy_id, true);
      setJdText(res.data.jd || ''); setJdMethod(res.data.method || '');
    } catch { toast.error('Regenerate failed'); } finally { setJdLoading(false); }
  };
  const saveJd = async () => {
    if (!jdVac) return;
    try { await vacancyApi.update(jdVac.vacancy_id, { jd: jdText }); toast.success('JD saved'); }
    catch { toast.error('Save failed'); }
  };
  const downloadJd = () => {
    const b = new Blob([jdText || ''], { type: 'text/plain;charset=utf-8' });
    const u = URL.createObjectURL(b);
    const a = document.createElement('a');
    a.href = u; a.download = `JD-${(jdVac?.designation || 'post').replace(/[^a-z0-9]+/gi, '-')}.txt`;
    a.click(); URL.revokeObjectURL(u);
  };
  const printJd = () => {
    const w = window.open('', '_blank');
    if (!w) { toast.error('Allow pop-ups'); return; }
    const safe = (jdText || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const title = String(jdVac?.designation || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>JD — ${title}</title><style>@page{size:A4;margin:20mm}body{font-family:Georgia,serif;font-size:13px;line-height:1.6;white-space:pre-wrap}h1{font-size:20px;border-bottom:2px solid #11182B;padding-bottom:6px}.b{font-family:sans-serif;margin-bottom:14px}@media print{.b{display:none}}</style></head><body><div class="b"><button onclick="window.print()">Print / Save as PDF</button></div><h1>Job Description — ${title}</h1>${safe}</body></html>`);
    w.document.close();
  };

  return (
    <div data-testid="requirement-tat-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <Timer className="w-6 h-6 text-[#5A6377]" />
              <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.035em] font-extrabold text-[#172554]">
                Vacancies &amp; Recruitment TAT
              </h1>
            </div>
            <p className="text-sm text-[#5A6377] mb-6">
              {activeUnit || 'All units'} · every open position, and how long each filled one took.
              Calculated from the vacancy and the joining date — <b>nothing on this page is entered by hand</b>.
            </p>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
              <Kpi icon={Hourglass} label="Open positions" value={s.open ?? '—'}
                   sub={s.longest_open_days != null ? `longest waiting ${s.longest_open_days} days` : null}
                   color="#E9A23B" bg="#FBF7EC" />
              <Kpi icon={CheckCircle2} label="Filled" value={s.filled ?? '—'} color="#15A36E" bg="#E7F7EF" />
              {/* Every tracked seat, not only the filled ones — a seat open 33
                  days is 33 days of waiting whether or not anyone has joined. */}
              <Kpi icon={Clock} label="Average TAT" value={s.average_days != null ? `${s.average_days}d` : '—'}
                   sub={s.average_basis
                     ? `${s.average_label} · ${s.average_basis}`
                     : 'no positions tracked yet'} color="#2563EB" bg="#E8F2FE" />
              <Kpi icon={Users} label="Positions tracked" value={(data?.rows || []).length}
                   color="#7C6BD6" bg="#EEEAFB" />
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Input data-testid="rtat-search" placeholder="Search person, designation or department…"
                     value={search} onChange={(e) => setSearch(e.target.value)}
                     className="max-w-xs border-[#E6E9F0]" />
              <div className="flex gap-1 bg-[#EFF1F6] rounded-lg p-1">
                {[['all', 'All'], ['open', 'Open'], ['filled', 'Filled']].map(([k, label]) => (
                  <button key={k} data-testid={`rtat-view-${k}`} onClick={() => setView(k)}
                          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                            view === k ? 'bg-white text-[#11182B] shadow-sm' : 'text-[#5A6377]'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : (
              <div className="bg-white border border-[#E6E9F0] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#102468] text-white text-xs font-semibold uppercase tracking-wide">
                      <tr>
                        <th className="py-3 px-4 text-left">Position vacated by</th>
                        <th className="py-3 px-4 text-left">Designation</th>
                        <th className="py-3 px-4 text-left">Department</th>
                        <th className="py-3 px-4 text-left">Vacancy created</th>
                        <th className="py-3 px-4 text-left">Vacancy filled</th>
                        <th className="py-3 px-4 text-left">New employee</th>
                        <th className="py-3 px-4 text-right">TAT (days)</th>
                        <th className="py-3 px-4 text-left">Duration</th>
                        <th className="py-3 px-4 text-left">Status</th>
                        {canManage && <th className="py-3 px-4 text-left">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.vacancy_id} data-testid={`rtat-row-${r.vacancy_id}`}
                            className="border-b border-[#E6E9F0] last:border-0 hover:bg-[#F7F8FB]">
                          <td className="py-3 px-4 text-sm text-[#11182B] font-medium">
                            {r.vacated_by
                              ? (r.vacated_by_employee_id
                                  ? <Link to={`/employees/${r.vacated_by_employee_id}`}
                                          className="text-[#2563EB] hover:underline">{r.vacated_by}</Link>
                                  : r.vacated_by)
                              : <span className="text-[#98A1B2]">New position</span>}
                            {r.last_working_day && (
                              <div className="text-[10px] text-[#98A1B2]">LWD {dmy(r.last_working_day)}</div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-[#5A6377]">{r.designation || '—'}</td>
                          <td className="py-3 px-4 text-sm text-[#5A6377]">{r.department || '—'}</td>
                          <td className="py-3 px-4 text-sm text-[#5A6377] whitespace-nowrap">{dmy(r.vacancy_created_date)}</td>
                          <td className="py-3 px-4 text-sm text-[#5A6377] whitespace-nowrap">
                            {r.vacancy_filled_date ? dmy(r.vacancy_filled_date) : <span className="text-[#C9CFDB]">—</span>}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {r.new_employee_name
                              ? <Link to={`/employees/${r.new_employee_id}`}
                                      className="text-[#2563EB] hover:underline">{r.new_employee_name}</Link>
                              : <span className="text-[#C9CFDB]">—</span>}
                          </td>
                          <td className="py-3 px-4 text-sm text-right font-semibold tabular-nums"
                              style={{ color: r.status === 'Filled' ? tatColour(r.tat_days) : '#8A5A12' }}>
                            {r.tat_days == null ? '—' : r.tat_days}
                          </td>
                          <td className="py-3 px-4 text-sm text-[#5A6377]">
                            {r.status === 'Filled'
                              ? (r.tat_label || '—')
                              : <span className="text-[#8A5A12]">{r.tat_label ? `open ${r.tat_label}` : 'not yet vacant'}</span>}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              r.status === 'Filled'
                                ? 'bg-[#E4F6EE] text-[#15A36E]'
                                : 'bg-[#FBF7EC] text-[#8A5A12]'}`}>
                              {r.status}
                            </span>
                          </td>
                          {canManage && (
                            <td className="py-3 px-4">
                              {/* Only an OPEN seat can be worked on. A filled one
                                  is a completed recruitment, and its record is
                                  what the TAT figure is read from. */}
                              {r.status === 'Open' ? (
                                <div className="flex gap-2">
                                  <Button data-testid={`vac-jd-${r.vacancy_id}`} variant="outline" size="sm"
                                          className="text-[#2563EB]" onClick={() => openJd(r)}>
                                    <FileText className="w-4 h-4 mr-1" />JD
                                  </Button>
                                  <Button data-testid={`vac-del-${r.vacancy_id}`} variant="outline" size="sm"
                                          className="text-[#E11D2A] border-[#F5C9CC]" onClick={() => removeVac(r)}>
                                    <Trash2 className="w-4 h-4 mr-1" />Close
                                  </Button>
                                </div>
                              ) : <span className="text-[11px] text-[#C9CFDB]">—</span>}
                            </td>
                          )}
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr><td colSpan={canManage ? 10 : 9} className="py-12 text-center text-[#98A1B2]">
                          No positions to report. A row appears here the moment a resignation is
                          recorded or a vacancy is opened.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <p className="text-[11px] text-[#98A1B2] mt-3 max-w-3xl">
              The clock starts the day the seat actually empties — the leaver&apos;s last working day,
              not the day the resignation was recorded. Notice period is time the seat is still
              occupied, and counting it would make every recruitment look a month slower than it was.
              It stops on the replacement&apos;s joining date, set when they are added from the vacancy
              in <b>Employees → Add from Vacancy</b>.
            </p>
          </div>
        </main>
      </div>

      <Dialog open={!!jdVac} onOpenChange={(o) => { if (!o) setJdVac(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Job Description — {jdVac?.designation}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-[#5A6377]">
              {jdLoading ? 'Generating…' : (jdMethod ? `Source: ${jdMethod === 'ai' ? 'AI-drafted' : 'template'}` : '')}
            </div>
            <textarea data-testid="vac-jd-text" value={jdText} onChange={(e) => setJdText(e.target.value)}
                      disabled={jdLoading} className="w-full h-80 border border-[#E6E9F0] rounded-md p-3 text-sm font-mono" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={regenJd} disabled={jdLoading} data-testid="vac-jd-regen">Regenerate (AI)</Button>
              <Button variant="outline" onClick={downloadJd} disabled={jdLoading || !jdText} data-testid="vac-jd-download">Download</Button>
              <Button variant="outline" onClick={printJd} disabled={jdLoading || !jdText} data-testid="vac-jd-print">Print</Button>
              <Button onClick={saveJd} disabled={jdLoading || !jdText} data-testid="vac-jd-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
